import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { setUser } from '../store/slices/authSlice';
import VoiceReRecord from './VoiceReRecord';
import VoiceCloning from './VoiceCloning';
import CommentSettings from './CommentSettings';
import ToneArchetypeSettings from './ToneArchetypeSettings';
import './Profile.css';

function Profile() {
  const currentUser = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language?.substring(0, 2) || 'it';
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    avatar_url: '',
    bio: '',
    preferred_language: currentLanguage,
    timezone: 'UTC'
  });
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [showVoiceCloning, setShowVoiceCloning] = useState(false);
  const [showCommentSettings, setShowCommentSettings] = useState(false);
  const [showToneArchetype, setShowToneArchetype] = useState(false);
  // When true, user is recording in AI personalization or voice cloning — disable "Start cloning"
  const [voiceRecordingInProgress, setVoiceRecordingInProgress] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Fetch current user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;
      
      try {
        setFetching(true);
        const response = await apiClient.get('/api/v1/auth/me');
        const userData = response.data;
        
        setFormData({
          full_name: userData.full_name || '',
          phone_number: userData.phone_number || '',
          avatar_url: userData.avatar_url || '',
          bio: userData.bio || '',
          preferred_language: userData.preferred_language || currentLanguage,
          timezone: userData.timezone || 'UTC'
        });
      } catch (err) {
        setError('Failed to fetch profile. Please try again.');
        console.error('Error fetching profile:', err);
      } finally {
        setFetching(false);
      }
    };
    
    fetchProfile();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Only send fields that have values or are being cleared
      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          updateData[key] = formData[key] || null;
        }
      });

      const response = await apiClient.put('/api/v1/auth/profile', updateData);
      
      // Update Redux store with new user data
      dispatch(setUser(response.data));
      
      setSuccess(true);
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to update profile. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <div className="loading-state">{t('profilePage.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <h1>{t('profilePage.title')}</h1>
          <p>{t('profilePage.subtitle')}</p>
        </div>

        <div id="profile-voice-sample" className="profile-section">
          <button
            type="button"
            className="profile-section-toggle"
            onClick={() => setShowVoiceSettings((v) => !v)}
            aria-expanded={showVoiceSettings}
          >
            <span className="profile-section-title">{t('profilePage.aiPersonalization.title')}</span>
            <span className="profile-section-hint">{t('profilePage.aiPersonalization.hint')}</span>
            <span className="profile-section-caret">{showVoiceSettings ? '▾' : '▸'}</span>
          </button>

          <div
            className={`profile-section-content ${showVoiceSettings ? 'open' : 'closed'}`}
          >
            <div className="profile-section-content-inner">
              <VoiceReRecord onRecordingChange={setVoiceRecordingInProgress} />
            </div>
          </div>
        </div>

        <div className="profile-section">
          <button
            type="button"
            className="profile-section-toggle"
            onClick={() => setShowVoiceCloning((v) => !v)}
            aria-expanded={showVoiceCloning}
          >
            <span className="profile-section-title">{t('profilePage.aiVoiceClone.title')}</span>
            <span className="profile-section-hint">{t('profilePage.aiVoiceClone.hint')}</span>
            <span className="profile-section-caret">{showVoiceCloning ? '▾' : '▸'}</span>
          </button>
          <div
            className={`profile-section-content ${showVoiceCloning ? 'open' : 'closed'}`}
          >
            <div className="profile-section-content-inner">
              <VoiceCloning
                externalRecordingInProgress={voiceRecordingInProgress}
                onRecordingChange={setVoiceRecordingInProgress}
              />
            </div>
          </div>
        </div>

        <div className="profile-section">
          <button
            type="button"
            className="profile-section-toggle"
            onClick={() => setShowToneArchetype((v) => !v)}
            aria-expanded={showToneArchetype}
          >
            <span className="profile-section-title">{t('profilePage.toneArchetype.title')}</span>
            <span className="profile-section-hint">{t('profilePage.toneArchetype.hint')}</span>
            <span className="profile-section-caret">{showToneArchetype ? '▾' : '▸'}</span>
          </button>
          <div
            className={`profile-section-content ${showToneArchetype ? 'open' : 'closed'}`}
          >
            <div className="profile-section-content-inner">
              <ToneArchetypeSettings />
            </div>
          </div>
        </div>

        <div className="profile-section">
          <button
            type="button"
            className="profile-section-toggle"
            onClick={() => setShowCommentSettings((v) => !v)}
            aria-expanded={showCommentSettings}
          >
            <span className="profile-section-title">{t('comments.settings')}</span>
            <span className="profile-section-hint">{t('comments.settingsDescription')}</span>
            <span className="profile-section-caret">{showCommentSettings ? '▾' : '▸'}</span>
          </button>
          <div
            className={`profile-section-content ${showCommentSettings ? 'open' : 'closed'}`}
          >
            <div className="profile-section-content-inner">
              <CommentSettings />
            </div>
          </div>
        </div>

        {success && (
          <div className="alert alert-success">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t('profilePage.saveSuccess')}
          </div>
        )}
        
        {error && (
          <div className="alert alert-error">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="full_name">{t('profilePage.form.fullName')}</label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              value={formData.full_name}
              onChange={handleChange}
              disabled={loading}
              placeholder={t('profilePage.form.fullNamePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone_number">{t('profilePage.form.phoneNumber')}</label>
            <input
              id="phone_number"
              name="phone_number"
              type="tel"
              value={formData.phone_number}
              onChange={handleChange}
              disabled={loading}
              placeholder="+1234567890"
            />
          </div>

          <div className="form-group">
            <label htmlFor="avatar_url">{t('profilePage.form.avatarUrl')}</label>
            <input
              id="avatar_url"
              name="avatar_url"
              type="url"
              value={formData.avatar_url}
              onChange={handleChange}
              disabled={loading}
              placeholder={t('profilePage.form.avatarPlaceholder')}
            />
            {formData.avatar_url && (
              <div className="avatar-preview">
                <img src={formData.avatar_url} alt="Avatar preview" onError={(e) => {
                  e.target.style.display = 'none';
                }} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="bio">{t('profilePage.form.bio')}</label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows="4"
              disabled={loading}
              placeholder={t('profilePage.form.bioPlaceholder')}
              maxLength={500}
            />
            <div className="char-count">{formData.bio.length}/500</div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="preferred_language">{t('profilePage.form.language')}</label>
              <select
                id="preferred_language"
                name="preferred_language"
                value={formData.preferred_language}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="en">English</option>
                <option value="it">Italiano</option>
                <option value="es">Español</option>
                <option value="de">Deutsch</option>
                <option value="fr">Français</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="timezone">{t('profilePage.form.timezone')}</label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (EST/EDT)</option>
                <option value="America/Chicago">Central Time (CST/CDT)</option>
                <option value="America/Denver">Mountain Time (MST/MDT)</option>
                <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Europe/Paris">Paris (CET/CEST)</option>
                <option value="Europe/Rome">Rome (CET/CEST)</option>
                <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Shanghai">Shanghai (CST)</option>
                <option value="Australia/Sydney">Sydney (AEDT/AEST)</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className={`submit-button ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? t('profilePage.form.saving') : t('profilePage.form.saveChanges')}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/')}
              disabled={loading}
            >
              {t('profilePage.form.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Profile;

